// src/components/CuratedLists.jsx
// Curated lists — create, view, share
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUser, API_BASE } from "../context/UserContext";
import { STATUS_EMOJI, FRONTEND_URL } from "../constants";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary: "#655d54", primaryDim: "#595149",
  bg: "#faf9f6", surfaceLow: "#f4f4f0",
  container: "#edeeea", containerLowest: "#ffffff",
  onSurface: "#2f3430", onSurfaceVariant: "#5c605c",
  outlineVariant: "#8a8e8a", primaryContainer: "#ede0d5",
};

export default function CuratedLists({ personalPlaces = [] }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const mobile = window.innerWidth <= 768;
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [selectedPlaces, setSelectedPlaces] = useState(new Set());
  const [expandedList, setExpandedList] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editPlaces, setEditPlaces] = useState(new Set());
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API_BASE}/lists/`)
      .then((res) => setLists(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    try {
      const res = await axios.post(`${API_BASE}/lists/`, {
        title: newTitle.trim(),
        description: newDesc.trim() || null,
        place_ids: [...selectedPlaces],
      });
      setLists((prev) => [res.data, ...prev]);
      setCreating(false);
      setNewTitle(""); setNewDesc(""); setSelectedPlaces(new Set());
    } catch (e) { alert(e.response?.data?.detail || t("curated.create_failed")); }
  };

  const handleDelete = async (listId) => {
    try {
      await axios.delete(`${API_BASE}/lists/${listId}`);
      setLists((prev) => prev.filter((l) => l.id !== listId));
    } catch (e) {}
  };

  const handleCopyLink = (listId) => {
    const url = `${FRONTEND_URL}/list/${listId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(listId);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleShare = async (listId, title) => {
    const url = `${FRONTEND_URL}/list/${listId}`;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (e) {
        if (e.name !== "AbortError") handleCopyLink(listId);
      }
    } else {
      handleCopyLink(listId);
    }
  };

  const startEdit = async (listId, e) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`${API_BASE}/lists/${listId}/mine`);
      const data = res.data;
      setEditingId(listId);
      setEditTitle(data.title);
      setEditDesc(data.description || "");
      setEditPlaces(new Set(data.places.map((p) => p.id)));
    } catch { /* ignore */ }
  };

  const saveEdit = async () => {
    if (!editTitle.trim() || !editingId) return;
    setEditSaving(true);
    try {
      // Update title/description
      await axios.patch(`${API_BASE}/lists/${editingId}`, {
        title: editTitle.trim(),
        description: editDesc.trim() || null,
      });

      // Get current places in the list
      const res = await axios.get(`${API_BASE}/lists/${editingId}/mine`);
      const currentIds = new Set(res.data.places.map((p) => p.id));

      // Remove places no longer selected
      for (const id of currentIds) {
        if (!editPlaces.has(id)) {
          await axios.delete(`${API_BASE}/lists/${editingId}/items/${id}`);
        }
      }
      // Add newly selected places
      for (const id of editPlaces) {
        if (!currentIds.has(id)) {
          await axios.post(`${API_BASE}/lists/${editingId}/items`, { place_id: id });
        }
      }

      // Refresh list
      const updated = await axios.get(`${API_BASE}/lists/`);
      setLists(updated.data);
      setEditingId(null);
      if (expandedList?.id === editingId) {
        const detail = await axios.get(`${API_BASE}/lists/${editingId}/mine`);
        setExpandedList(detail.data);
      }
    } catch { /* ignore */ }
    setEditSaving(false);
  };

  const toggleEditPlace = (placeId) => {
    setEditPlaces((prev) => {
      const next = new Set(prev);
      if (next.has(placeId)) next.delete(placeId);
      else next.add(placeId);
      return next;
    });
  };

  const toggleExpand = async (listId) => {
    if (expandedList?.id === listId) { setExpandedList(null); return; }
    try {
      const res = await axios.get(`${API_BASE}/lists/${listId}`);
      setExpandedList(res.data);
    } catch (e) {}
  };

  const togglePlace = (placeId) => {
    setSelectedPlaces((prev) => {
      const next = new Set(prev);
      if (next.has(placeId)) next.delete(placeId);
      else next.add(placeId);
      return next;
    });
  };

  return (
    <div style={{ padding: mobile ? "16px" : "0" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0, fontFamily: FH, fontSize: 20, fontWeight: 400, color: C.onSurface }}>
            {t("curated.title")}
          </h3>
        </div>
        <button onClick={() => setCreating(!creating)} style={{
          padding: "4px 10px", border: "none", borderRadius: 6,
          background: creating ? C.container : C.primary,
          color: creating ? C.onSurfaceVariant : "#fff6ef",
          fontFamily: FL, fontSize: 14, fontWeight: 700, cursor: "pointer",
        }}>
          {creating ? t("curated.cancel") : t("curated.new_list")}
        </button>
      </div>

      {/* Create new list */}
      {creating && (
        <div style={{
          background: C.containerLowest, borderRadius: 12, padding: 20,
          marginBottom: 20, boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
        }}>
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t("curated.list_title_hint")}
            style={{
              width: "100%", padding: "12px", background: C.surfaceLow,
              border: "none", borderRadius: 8, fontFamily: FL, fontSize: 16,
              color: C.onSurface, boxSizing: "border-box", marginBottom: 10,
            }}
          />
          <input
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder={t("curated.desc_hint")}
            style={{
              width: "100%", padding: "10px 12px", background: C.surfaceLow,
              border: "none", borderRadius: 8, fontFamily: FL, fontSize: 16,
              color: C.onSurface, boxSizing: "border-box", marginBottom: 14,
            }}
          />
          <p style={{ margin: "0 0 8px", fontFamily: FL, fontSize: 12, fontWeight: 700, color: C.outlineVariant, textTransform: "uppercase", letterSpacing: "0.1em" }}>
            {t("curated.select_places")} ({selectedPlaces.size})
          </p>
          <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 14 }}>
            {personalPlaces.map((p) => (
              <label key={p.id} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "8px 6px", cursor: "pointer",
                borderBottom: `1px solid ${C.container}`,
              }}>
                <input
                  type="checkbox"
                  checked={selectedPlaces.has(p.id)}
                  onChange={() => togglePlace(p.id)}
                  style={{ accentColor: C.primary }}
                />
                <span style={{ fontFamily: FL, fontSize: 16, color: C.onSurface }}>
                  {STATUS_EMOJI[p.status]} {p.name}
                </span>
              </label>
            ))}
          </div>
          <button onClick={handleCreate} disabled={!newTitle.trim()} style={{
            width: "100%", padding: "12px", border: "none", borderRadius: 8,
            background: newTitle.trim() ? C.primary : C.container,
            color: newTitle.trim() ? "#fff6ef" : C.outlineVariant,
            fontFamily: FL, fontSize: 16, fontWeight: 700, cursor: "pointer",
          }}>
            {t("curated.create")}
          </button>
        </div>
      )}

      {/* List items */}
      {loading ? (
        <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 16, color: C.outlineVariant }}>Loading...</p>
      ) : lists.length === 0 && !creating ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 36, color: "#d6d3d1", display: "block", marginBottom: 12 }}>bookmark</span>
          <p style={{ fontFamily: FH, fontStyle: "italic", fontSize: 17, color: C.outlineVariant }}>
            No lists yet
          </p>
          <p style={{ fontFamily: FL, fontSize: 14, color: C.outlineVariant }}>
            Organize your favorite places by theme
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {lists.map((list) => (
            <div key={list.id} style={{
              background: C.containerLowest, borderRadius: 12,
              overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}>
              <div
                onClick={() => toggleExpand(list.id)}
                style={{
                  padding: "14px 16px", cursor: "pointer",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                <div>
                  <p style={{ margin: 0, fontFamily: FH, fontSize: 17, fontWeight: 600, color: C.onSurface }}>
                    {list.title}
                  </p>
                  <p style={{ margin: "3px 0 0", fontFamily: FL, fontSize: 14, color: C.outlineVariant }}>
                    {list.item_count} places {list.description ? `· ${list.description}` : ""}
                  </p>
                </div>
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <button onClick={(e) => { e.stopPropagation(); handleCopyLink(list.id); }} style={{
                    background: copiedId === list.id ? C.primaryContainer : "none",
                    border: "none", cursor: "pointer", padding: "6px",
                    color: copiedId === list.id ? C.primary : C.outlineVariant,
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{copiedId === list.id ? "check" : "link"}</span>
                  </button>
                  {navigator.share && (
                    <button onClick={(e) => { e.stopPropagation(); handleShare(list.id, list.title); }} style={{
                      background: "none", border: "none", cursor: "pointer", padding: "6px",
                      color: C.outlineVariant,
                    }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 18 }}>ios_share</span>
                    </button>
                  )}
                  <button onClick={(e) => startEdit(list.id, e)} style={{
                    background: "none", border: "none", cursor: "pointer", padding: "6px",
                    color: C.outlineVariant,
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>edit</span>
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(list.id); }} style={{
                    background: "none", border: "none", cursor: "pointer", padding: "6px",
                    color: C.outlineVariant,
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                  </button>
                </div>
              </div>

              {/* Edit form */}
              {editingId === list.id && (
                <div style={{ borderTop: `1px solid ${C.container}`, padding: 16 }}>
                  <input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder={t("curated.list_title")}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: "100%", padding: "10px 12px", background: C.surfaceLow,
                      border: "none", borderRadius: 8, fontFamily: FL, fontSize: 16,
                      color: C.onSurface, boxSizing: "border-box", marginBottom: 8,
                    }}
                  />
                  <input
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    placeholder={t("curated.desc_hint")}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: "100%", padding: "8px 12px", background: C.surfaceLow,
                      border: "none", borderRadius: 8, fontFamily: FL, fontSize: 16,
                      color: C.onSurface, boxSizing: "border-box", marginBottom: 12,
                    }}
                  />
                  <p style={{ margin: "0 0 6px", fontFamily: FL, fontSize: 12, fontWeight: 700, color: C.outlineVariant, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                    Places ({editPlaces.size})
                  </p>
                  <div style={{ maxHeight: 200, overflowY: "auto", marginBottom: 12 }}>
                    {personalPlaces.map((p) => (
                      <label key={p.id} onClick={(e) => e.stopPropagation()} style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "6px 4px", cursor: "pointer",
                        borderBottom: `1px solid ${C.container}`,
                      }}>
                        <input
                          type="checkbox"
                          checked={editPlaces.has(p.id)}
                          onChange={() => toggleEditPlace(p.id)}
                          style={{ accentColor: C.primary }}
                        />
                        <span style={{ fontFamily: FL, fontSize: 16, color: C.onSurface }}>
                          {STATUS_EMOJI[p.status]} {p.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={(e) => { e.stopPropagation(); saveEdit(); }} disabled={editSaving || !editTitle.trim()} style={{
                      flex: 1, padding: "10px", border: "none", borderRadius: 8,
                      background: editTitle.trim() ? C.primary : C.container,
                      color: editTitle.trim() ? "#fff6ef" : C.outlineVariant,
                      fontFamily: FL, fontSize: 16, fontWeight: 700, cursor: "pointer",
                    }}>
                      {editSaving ? "Saving..." : "Save"}
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setEditingId(null); }} style={{
                      padding: "10px 16px", border: "none", borderRadius: 8,
                      background: C.container, color: C.onSurfaceVariant,
                      fontFamily: FL, fontSize: 16, fontWeight: 600, cursor: "pointer",
                    }}>
                      {t("curated.cancel")}
                    </button>
                  </div>
                </div>
              )}

              {/* Expanded place list */}
              {editingId !== list.id && expandedList?.id === list.id && expandedList.places && (
                <div style={{ borderTop: `1px solid ${C.container}`, padding: "8px 0" }}>
                  {expandedList.places.map((p) => (
                    <div key={p.id} style={{ padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 13 }}>{STATUS_EMOJI[p.status]}</span>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontFamily: FH, fontSize: 16, color: C.onSurface }}>{p.name}</p>
                        {p.note && <p style={{ margin: "2px 0 0", fontFamily: FH, fontStyle: "italic", fontSize: 14, color: C.outlineVariant }}>"{p.note}"</p>}
                      </div>
                      {p.rating > 0 && (
                        <span style={{ fontFamily: FL, fontSize: 14, color: C.primary }}>{"★".repeat(p.rating)}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
